/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 25th 2022, 8:56:43 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import GroupsIcon from "@mui/icons-material/Groups";
import { PermissionsTransferListInput } from "authorization";
import { withAccessCheck } from "authorization/hoc";
import { Team } from "lib/schemas";
import { useTeamAPIForm } from "teams/services/TeamsAPI";
import {
  OSAPIForm,
  OSAPIFormProps,
  TextFormInput,
  FormSubmitSuccessSnackbar,
  FormSubmitBubblingStopper,
} from "ui-widgets/forms";
import { StyledOSDialog, StyledOSDialogProps } from "ui-widgets";
import { WorkersTransferListInput } from "workers";

export type TeamFormProps = Pick<OSAPIFormProps<Team>, "fieldsToShow" | "submitButtonText"> &
  Pick<StyledOSDialogProps, "open" | "onClose" | "popUpTitle" | "popUpDescription"> & {
    team: Team;
    onTeamSaveResponse: (team: Team) => void;
    successMessage?: string;
  };

export const TeamForm = ({
  team,
  open,
  onClose,
  onTeamSaveResponse,
  popUpTitle,
  popUpDescription,
  fieldsToShow,
  submitButtonText,
  successMessage,
}: TeamFormProps) => {
  const onSave = (updatedTeam: Team) => {
    onTeamSaveResponse(updatedTeam);
    onClose(null, "backdropClick");
  };

  const useTeamAPIFormReturn = useTeamAPIForm({ team, updateSelectedTeam: onSave });

  return (
    <>
      {successMessage ? (
        <FormSubmitSuccessSnackbar
          isSuccessfulAPIDataSend={useTeamAPIFormReturn.isSuccessfulAPIDataSend}
          successMessage={successMessage}
          alertProps={{ horizontal: "center", vertical: "top" }}
        />
      ) : null}

      <StyledOSDialog
        popUpIcon={<GroupsIcon color="secondary" />}
        popUpTitle={popUpTitle}
        popUpDescription={popUpDescription}
        open={open}
        onClose={onClose}
      >
        <FormSubmitBubblingStopper>
          <OSAPIForm
            useAPIFormReturn={useTeamAPIFormReturn}
            submitButtonText={submitButtonText}
            fieldsToShow={fieldsToShow}
            showFeedbackAlerts={false}
          >
            <TextFormInput<Team> schema_field_key="team_name" label="Team name" />
            <TeamFormFieldsWithAccessCheck />
          </OSAPIForm>
        </FormSubmitBubblingStopper>
      </StyledOSDialog>
    </>
  );
};

const TeamFormFieldsWithAccessCheck = withAccessCheck(
  {
    resourceId: "manageTeams",
    invalidTitle: "Need more control over your team?",
    invalidMessage: (
      <>
        Upgrade now to manage members and permissions for your team. Add/remove members to teams, Manage the permissions
        that you want team members to have. Get more with an upgraded plan!
      </>
    ),
  },
  () => (
    <>
      <WorkersTransferListInput<Team>
        schema_field_key="members"
        label="Members"
        getSearchableName={(worker) => `${worker.first_name} ${worker.last_name} ${worker.email} ${worker.username}`}
      />
      <PermissionsTransferListInput<Team> schema_field_key="permissions" label="Permissions" />
    </>
  )
);
